import {CommonModule} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {Component, DestroyRef, Inject, OnInit, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {Integration} from '@gatewee/types/integration';
import {Pool} from '@gatewee/types/pool';
import {PaymentProvider} from '@gatewee/types/provider';
import {AssociateWalletPayload, Wallet} from '@gatewee/types/wallet';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeSelectV3Component} from '../../gatewee-select-v3/gatewee-select-v3.component';
import {WalletBindingMode} from '../form/wallet-form.component';

export interface WalletAssociateFormDialogData {
  walletId: number;
  recipientId: number;
  fetchIntegrationsFn: (
    entityId: number,
    inUse?: boolean,
  ) => Observable<Integration[]>;
  fetchPoolsFn: () => Observable<Pool[]>;
  associateFn: (
    walletId: number,
    payload: AssociateWalletPayload,
  ) => Observable<Wallet>;
}

@Component({
  selector: 'app-wallet-associate-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeSelectV3Component,
  ],
  templateUrl: './wallet-associate-form.component.html',
  styleUrls: ['./wallet-associate-form.component.css'],
})
export class WalletAssociateFormComponent implements OnInit {
  form!: FormGroup;
  submitting = false;
  loadingOptions = true;
  loadError: string | null = null;

  integrationItems: {value: number; label: string}[] = [];
  poolItems: {value: number; label: string}[] = [];

  readonly bindingModeItems = [
    {
      value: 'standalone',
      label: 'features.wallets.create.bindingModeStandalone',
    },
    {
      value: 'pooled',
      label: 'features.wallets.create.bindingModePooled',
    },
  ];

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<
      Wallet | false,
      WalletAssociateFormDialogData
    >,
    private readonly fb: FormBuilder,
    private readonly alerts: TuiAlertService,
    private readonly translate: TranslateService,
  ) {}

  get data(): WalletAssociateFormDialogData {
    return this.context.data;
  }

  get bindingMode(): WalletBindingMode {
    return this.form?.get('bindingMode')?.value ?? 'standalone';
  }

  get isStandaloneBinding(): boolean {
    return this.bindingMode === 'standalone';
  }

  ngOnInit(): void {
    this.buildForm();
    this.loadBindingOptions(this.bindingMode);

    this.form
      .get('bindingMode')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((mode: WalletBindingMode) => {
        this.applyBindingModeValidators(mode);
        this.loadBindingOptions(mode);
      });
  }

  private buildForm(): void {
    this.form = this.fb.group({
      bindingMode: ['standalone' as WalletBindingMode, [Validators.required]],
      integrationId: [null as number | null],
      poolId: [null as number | null],
    });

    this.applyBindingModeValidators('standalone');
  }

  private applyBindingModeValidators(mode: WalletBindingMode): void {
    const integrationId = this.form.get('integrationId');
    const poolId = this.form.get('poolId');

    if (mode === 'standalone') {
      integrationId?.setValidators([Validators.required]);
      poolId?.clearValidators();
      poolId?.setValue(null, {emitEvent: false});
    } else {
      poolId?.setValidators([Validators.required]);
      integrationId?.clearValidators();
      integrationId?.setValue(null, {emitEvent: false});
    }

    integrationId?.updateValueAndValidity({emitEvent: false});
    poolId?.updateValueAndValidity({emitEvent: false});
  }

  private loadBindingOptions(mode: WalletBindingMode): void {
    this.loadingOptions = true;
    this.loadError = null;
    this.integrationItems = [];
    this.poolItems = [];

    if (mode === 'standalone') {
      this.loadStandaloneIntegrations();
      return;
    }

    this.loadPools();
  }

  private loadStandaloneIntegrations(): void {
    this.data.fetchIntegrationsFn(this.data.recipientId, false).subscribe({
      next: available => {
        if (available.length) {
          this.integrationItems = available.map(integration =>
            this.toIntegrationItem(integration),
          );
          this.loadingOptions = false;
          return;
        }

        this.data.fetchIntegrationsFn(this.data.recipientId).subscribe({
          next: all => {
            this.loadingOptions = false;
            this.loadError = this.translate.instant(
              all.length
                ? 'features.wallets.create.noAvailableIntegrations'
                : 'features.wallets.create.noIntegrations',
            );
          },
          error: (error: HttpErrorResponse) => {
            this.loadingOptions = false;
            this.loadError =
              error.error?.message ??
              this.translate.instant('common.errorLoadingData');
          },
        });
      },
      error: (error: HttpErrorResponse) => {
        this.loadingOptions = false;
        this.loadError =
          error.error?.message ??
          this.translate.instant('common.errorLoadingData');
      },
    });
  }

  private loadPools(): void {
    this.data.fetchPoolsFn().subscribe({
      next: pools => {
        this.poolItems = pools.map(pool => this.toPoolItem(pool));
        this.loadingOptions = false;
        if (!this.poolItems.length) {
          this.loadError = this.translate.instant(
            'features.wallets.create.noPools',
          );
        }
      },
      error: (error: HttpErrorResponse) => {
        this.loadingOptions = false;
        this.loadError =
          error.error?.message ??
          this.translate.instant('common.errorLoadingData');
      },
    });
  }

  private providerLabel(provider: PaymentProvider | string): string {
    const slug = String(provider).trim().toLowerCase();
    const key = `enums.paymentProvider.${slug}`;
    const translated = this.translate.instant(key);
    return !translated || translated === key
      ? this.translate.instant('common.unknown')
      : translated;
  }

  private toIntegrationItem(integration: Integration): {
    value: number;
    label: string;
  } {
    const providerLabel = this.providerLabel(integration.provider);
    const suffix = integration.description
      ? ` — ${integration.description}`
      : '';
    return {
      value: integration.id,
      label: `#${integration.id} · ${providerLabel}${suffix}`,
    };
  }

  private toPoolItem(pool: Pool): {value: number; label: string} {
    const provider =
      'provider' in pool.integration
        ? pool.integration.provider
        : PaymentProvider.SANDBOX;
    const providerLabel = this.providerLabel(provider);
    return {
      value: pool.id,
      label: `#${pool.id} · ${pool.label} · ${providerLabel}`,
    };
  }

  submit(): void {
    if (this.form.invalid || this.submitting || this.loadError) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const payload: AssociateWalletPayload = {};

    if (raw.bindingMode === 'standalone') {
      payload.integrationId = Number(raw.integrationId);
    } else {
      payload.poolId = Number(raw.poolId);
    }

    this.submitting = true;
    this.data.associateFn(this.data.walletId, payload).subscribe({
      next: updated => {
        this.submitting = false;
        this.alerts
          .open(this.translate.instant('features.wallets.associate.success'), {
            label: this.translate.instant('common.status.success'),
            appearance: 'success',
          })
          .subscribe();
        this.context.completeWith(updated);
      },
      error: (error: HttpErrorResponse) => {
        this.submitting = false;
        this.alerts
          .open(
            error.error?.message ??
              this.translate.instant('features.wallets.associate.error'),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
      },
    });
  }

  cancel(): void {
    this.context.completeWith(false);
  }
}
